import { styled } from '@mui/material';
import { useDialog } from 'contexts/dialog';
import { useViewport } from 'contexts/viewport';
import { omit } from 'lodash';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Icon,
    IconButton,
    MenuItem,
    Paper,
    RichTextArea,
    Select,
    Stack,
    Tag,
    Text,
    TextInput,
    Tooltip,
} from 'shell/components';
import {
    CITATION_SELECT_OPTIONS,
    CONTENT_BANK_RESOURCES_MB_LIMIT,
    DESCRIPTION_WORD_COUNT_LIMIT,
} from 'utils/constants';
import { bytesToMB, getHTMLWordCount, getTooltipComponent } from 'utils/helper';

const GeneralSection = memo(
    ({
        isEditable,
        form,
        setForm,
        errors,
        setErrors,
        setSelectRubricDialog,
        setEditRubricDialog,
        setEditBaselineDialog,
        setUploadContentBankResourcesDialog,
        setEditContentBankResourcesDialog,
    }) => {
        const { setDialog, closeDialog } = useDialog();
        const { device } = useViewport();
        const { t } = useTranslation('assignment');

        const updateForm = (key, value) => {
            setForm((current) => ({
                ...current,
                [key]: value,
            }));
        };

        const handleClickEditRubric = () => {
            if (!form.baseline) return setEditRubricDialog({ open: true });
            setDialog({
                open: true,
                message: t(
                    'dialog.rubric-modifications-reset-baseline-warning.title',
                    'Rubric Modifications Will Reset the Baseline'
                ),
                description: t(
                    'dialog.rubric-modifications-reset-baseline-warning.description',
                    'Modifying the rubric will require re-grading the baseline. Do you want to continue?'
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.continue', 'Continue'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    setEditRubricDialog({ open: true });
                    closeDialog();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        };

        const handleClickDeleteRubric = () => {
            setDialog({
                open: true,
                message: t('dialog.delete-rubric.title', 'Delete Rubric?'),
                description: t('dialog.delete-rubric.description', 'Are you sure you want to delete this rubric?'),
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.delete', 'Delete'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    setForm((current) => ({
                        ...current,
                        rubric: null,
                        baseline: null,
                        feedbackSettings: { ...current.feedbackSettings, templated: null },
                        gradingSettings: null,
                    }));
                    closeDialog();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        };

        const handleClickEditBaseline = () => {
            setEditBaselineDialog({ open: true });
        };

        const handleClickDeleteBaseline = () => {
            setDialog({
                open: true,
                message: t('dialog.delete-baseline.title', 'Delete Baseline?'),
                description: t('dialog.delete-baseline.description', 'Are you sure you want to delete this baseline?'),
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.delete', 'Delete'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    setForm((current) => ({ ...current, baseline: null }));
                    setErrors((current) => omit(current, ['baseline']));
                    closeDialog();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        };

        return (
            <RootStack sx={{ width: '100%' }} device={device} spacing={1}>
                <Text variant={'p1'}>{t('general-section.title', 'General Assignment Details')}</Text>
                <Paper variant={'outlined'}>
                    <Stack spacing={4}>
                        <CustomTextInput
                            error={errors.title}
                            placeholder={t('general-section.assignment-title-placeholder', 'Enter assignment title')}
                            value={form.title}
                            onChange={(e) => updateForm('title', e.target.value)}
                        />
                        <RichTextArea
                            aria-label={'assignment description'}
                            disabled={!isEditable}
                            error={
                                errors.description || getHTMLWordCount(form.description) > DESCRIPTION_WORD_COUNT_LIMIT
                            }
                            label={t('general-section.assignment-description-label', 'Assignment Description')}
                            helperText={t('remaining-word-count', '{{wordLimitStatus}} words', {
                                wordLimitStatus: `${getHTMLWordCount(form.description)}/${DESCRIPTION_WORD_COUNT_LIMIT}`,
                            })}
                            helperTextPosition={'end'}
                            placeholder={t(
                                'general-section.assignment-description-placeholder',
                                'Enter assignment details'
                            )}
                            required
                            value={form.description}
                            onChange={(value) => updateForm('description', value)}
                        />
                        <Stack spacing={2}>
                            <Text>
                                {t('assignment-rubric-label', 'Assignment Rubric')}{' '}
                                <Text color={'error'} component={'span'}>
                                    *
                                </Text>
                            </Text>
                            {form.rubric ? (
                                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                    <Text color={'light'}>{form.rubric.title}</Text>
                                    <Stack direction={'row'} spacing={2}>
                                        <IconButton
                                            aria-label={'edit rubric'}
                                            disabled={!isEditable}
                                            kind={'edit'}
                                            size={'small'}
                                            variant={'square'}
                                            onClick={handleClickEditRubric}
                                        />
                                        <IconButton
                                            aria-label={'delete rubric'}
                                            disabled={!isEditable}
                                            kind={'delete'}
                                            size={'small'}
                                            variant={'square'}
                                            onClick={handleClickDeleteRubric}
                                        />
                                    </Stack>
                                </Stack>
                            ) : (
                                <CustomButton
                                    color={errors.rubric ? 'destructive' : 'primary'}
                                    startIcon={<Icon kind={'add'} />}
                                    variant={'borderless'}
                                    onClick={() => setSelectRubricDialog({ open: true })}
                                >
                                    {t('general-section.add-a-rubric', 'Add a Rubric')}
                                </CustomButton>
                            )}
                        </Stack>
                        {form.baseline && (
                            <Stack spacing={2}>
                                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                    <Text>{t('general-section.baseline', 'Baseline')}</Text>
                                    <Tooltip
                                        content={getTooltipComponent(
                                            t('general-section.creating-a-baseline', 'Creating a Baseline'),
                                            t(
                                                'general-section.creating-a-baseline-description',
                                                'For optimal AI feedback and grading outcomes, we recommend uploading and grading a previous assignment. This will help align the AI with your assessment practices.'
                                            )
                                        )}
                                        type={'info'}
                                    />
                                    <Tag type={'general'} value={'recommended'} />
                                </Stack>
                                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                    <Text color={errors.baseline ? 'error' : 'light'}>
                                        {errors.baseline
                                            ? t(
                                                  'general-section.assignment-baseline-with-error',
                                                  'Assignment Baseline {{error}}',
                                                  { error: errors.baseline }
                                              )
                                            : t('general-section.assignment-baseline', 'Assignment Baseline')}
                                    </Text>
                                    <Stack direction={'row'} spacing={2}>
                                        <IconButton
                                            aria-label={'edit baseline'}
                                            disabled={!isEditable}
                                            kind={'edit'}
                                            size={'small'}
                                            variant={'square'}
                                            onClick={handleClickEditBaseline}
                                        />
                                        <IconButton
                                            aria-label={'delete baseline'}
                                            disabled={!isEditable}
                                            kind={'delete'}
                                            size={'small'}
                                            variant={'square'}
                                            onClick={handleClickDeleteBaseline}
                                        />
                                    </Stack>
                                </Stack>
                            </Stack>
                        )}
                        <CustomSelect
                            disabled={!isEditable}
                            label={
                                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                    <Text>{t('citation-format.label', 'Citation Format')}</Text>
                                    <Tooltip
                                        content={t(
                                            'citation-format.tooltip',
                                            'Grading adapts to your chosen citation style and applies the correct formatting standards (e.g., APA first-page formatting is evaluated differently).'
                                        )}
                                        type={'info'}
                                    />
                                </Stack>
                            }
                            labelId={'citation-format-select'}
                            inputProps={{
                                'aria-label': `select a citation format`,
                            }}
                            value={form?.citationFormat}
                            onChange={(e) => updateForm('citationFormat', e.target.value)}
                        >
                            {CITATION_SELECT_OPTIONS.map(({ key, value }, index) => (
                                <MenuItem key={index} value={key}>
                                    {t(`citation-format.option.${key}`, value)}
                                </MenuItem>
                            ))}
                        </CustomSelect>
                        <Stack spacing={2}>
                            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                <Text>{t('content-bank.title', 'Content Bank')}</Text>
                                <Tooltip
                                    content={t(
                                        'content-bank.tooltip',
                                        'Adding files to your Content Bank allows our AI to retrieve relevant context from these documents, enabling it to generate more personalized and accurate feedback and grading suggestions.'
                                    )}
                                    type={'info'}
                                />
                                <Text color={'light'}>
                                    [
                                    {t('content-bank.mb-used', '{{mbUsed}} MB used', {
                                        mbUsed: `${bytesToMB(
                                            form.contentBankResources
                                                .filter(({ active }) => active)
                                                .reduce((acc, cur) => acc + cur.data.size, 0)
                                        )}/${CONTENT_BANK_RESOURCES_MB_LIMIT}`,
                                    })}
                                    ]
                                </Text>
                            </Stack>
                            {form.contentBankResources.filter(({ active }) => active).length ? (
                                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                    <Text color={'light'}>
                                        {t('content-bank.files-added', '{{count}} file(s) added', {
                                            count: form.contentBankResources.filter(({ active }) => active).length,
                                        })}
                                    </Text>
                                    <Stack direction={'row'} spacing={2}>
                                        <IconButton
                                            aria-label={'edit content bank'}
                                            disabled={!isEditable}
                                            kind={'edit'}
                                            size={'small'}
                                            variant={'square'}
                                            onClick={() => setEditContentBankResourcesDialog({ open: true })}
                                        />
                                    </Stack>
                                </Stack>
                            ) : null}
                            <CustomButton
                                disabled={!isEditable}
                                startIcon={<Icon kind={'add'} />}
                                variant={'borderless'}
                                onClick={() => setUploadContentBankResourcesDialog({ open: true })}
                            >
                                {t('general-section.add-file', 'Add file')}
                            </CustomButton>
                        </Stack>
                    </Stack>
                </Paper>
            </RootStack>
        );
    }
);

const RootStack = styled(Stack)(() => {
    return {
        width: '100%',
    };
});

const CustomTextInput = styled(TextInput)(({ theme }) => {
    return {
        margin: theme.spacing(-2, -3),
        width: `calc(100% + ${theme.spacing(6)})`,
        '&& input': {
            fontSize: `${theme.typography.sh1.fontSize.large}rem`,
            fontWeight: theme.typography.sh1.fontWeight.semibold,
        },
        '&& fieldset': {
            borderColor: 'transparent',
        },
    };
});

const CustomButton = styled(Button)(() => {
    return {
        width: 'fit-content',
    };
});

const CustomSelect = styled(Select)(() => {
    return {
        '& > .MuiInputBase-root': {
            maxWidth: '360px',
        },
    };
});

export default GeneralSection;
